import React, { useEffect } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BsInfoCircle } from "react-icons/bs";
import useModernKpiGraphCard from "../../../core/hooks/useModernKpiGraphCard";
import Utils from "../../../core/utilities/Utils";
import "./ModernKpiGraphCard.scss";

const ModernKpiGraphCard = ({ t, graphData }) => {
  const {
    renderMoreOptions,
    getChart,
    renderDisplayModal,
    renderInfoModal,
    setChartData,
    setChartOptions,
  } = useModernKpiGraphCard(t);

  useEffect(() => {
    setupDataForTheGraph(graphData);
  }, [graphData]);

  const setupDataForTheGraph = (g) => {
    if (g?.type === 'LINE') {
      setupLineChartData(g);
    }
  };

  const setupLineChartData = (g) => {
    const dataPoints = g?.legends?.sort((a, b) => a.order - b.order);
    const datasets = [];
    dataPoints[0]?.yvalues?.forEach((yValue, index) => {
      const color = Utils.getDecodedHexForGraphColor(getGraphColor(yValue.label));
      datasets.push({
        label: labelTostring(yValue.label),
        borderColor: color.borderColor,
        backgroundColor: '#FFF',
        data: [],
        fill: yValue.label === 'ACTUAL',
        borderDash: yValue.label === 'ACTUAL' ? [] : [5, 5],
        borderWidth: yValue.label === 'ACTUAL' ? 2 : 1.5
      });
    });
    dataPoints?.forEach(dp => {
      dp?.yvalues?.forEach(yValue => {
        const dataset = datasets?.find(ds => ds.label === labelTostring(yValue.label));
        dataset.data.push(yValue.value);
      });
    });

    const labels = [];
    dataPoints?.forEach(dp => {
      labels.push(dp.xvalue?.substring(0, 3));
    });

    const cOption = {
      responsive: true,
      plugins: {
        legend: {
          position: "bottom",
          align: "start"
        },
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'top',
          color: 'black',
          font: {
            size: 10
          },
          formatter: function (value, context) {
            return value;
          }
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              return ` ${tooltipItem?.dataset?.data[tooltipItem.dataIndex]} ${graphData.uom ?? ''}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            color: 'rgba(200, 200, 200, 0.2)'
          }
        },
        y: {
          grid: {
            color: 'rgba(200, 200, 200, 0.2)'
          }
        },
      }
    };
    setChartOptions(cOption);
    setChartData({
      labels,
      datasets: datasets,
    });
  };

  const labelTostring = (label) => {
    switch (label) {
      case 'ACTUAL': return 'Actual';
      case 'TARGET': return 'Target';
      case 'RED_FLAG': return 'Red Flag';
      default: return label;
    }
  };

  const getGraphColor = (label) => {
    switch (label) {
      case 'ACTUAL': return 'BLUE';
      case 'TARGET': return 'GREEN';
      case 'RED_FLAG': return 'RED';
      default: return 'BLUE';
    }
  };

  const graphInfo = (
    <Popover id="popover-trigger-hover-focus" className="p-2">
      <div className="p-1">{graphData?.title}</div>
    </Popover>
  );

  const renderGraphIconHolder = () => {
    return (
      <div className='modern-kpi-icon-box'>
        {renderMoreOptions({
          graphType: graphData?.type,
          graphTitle: graphData?.title,
          id: graphData?.id,
          graphConfigType: graphData?.graphConfigType,
          graphInfo: graphData?.title
        })}
      </div>
    );
  };

  return (
    <div className={`modern-kpi-card01`} data-testid="modern-kpi-graph-card">
      <div className="modern-kpi-card-header">
        <h6 className="modern-kpi-card-title">{graphData?.title}</h6>
        {renderGraphIconHolder()}
      </div>
      <div className={`modern-kpi-card-body`}>
        <div className="modern-kpi-chart-container">{getChart(graphData?.type)}</div>
      </div>
      {renderDisplayModal('KPI_GRAPH', graphData)}
      {renderInfoModal()}
    </div>
  );
};

export default withTranslation()(ModernKpiGraphCard); 